import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqData = [
  {
    question: "Do you offer a free trial or a money-back guarantee?",
    answer:
      "Yes! We offer a 3-day free trial so you can test our AI agents risk-free. Plus, we provide a 2-week full money-back guarantee, no questions asked. You can start using Hubflo AI Agents today, and if it's not the right fit, we'll refund you in full.",
  },
  {
    question: "How complex is it to configure a Hubflo AI Agent?",
    answer:
      "One of the core benefits of Hubflo AI Agents is how easy they are to configure. The platform isn't complex at all. We offer two pre-configured AI employees already trained for different industries, along with a variety of templates. You can get started in just a few minutes by selecting one of these employees and connecting your website. From there, you can gradually refine and add more behavior, making it powerful yet incredibly easy to use.",
  },
  {
    question: "How can Hubflo AI Agents help my business?",
    answer:
      "Hubflo AI Agents come with two default virtual employees: Lexi and Corey. Lexi is your lead intake specialist, turning website visitors into qualified leads by engaging them, booking appointments, and qualifying prospects on autopilot. Corey is your customer support specialist, answering client questions based on your documents, website, and custom instructions, and seamlessly handing off complex issues to you. Plus, you can create fully custom agents tailored to your industry with our ready-made templates.",
  },
  {
    question: "How customizable are the agents?",
    answer:
      "You can fully customize your agents in numerous ways. We offer more than 30 different settings, allowing you to tailor the agent's instructions, add custom behaviors, and connect it to your website or external resources like Google. You can also customize the UI by adding your banner, logo, custom colors, and avatars. You can fine-tune the agent's personality, response length, tone, and creativity level. This makes Hubflo AI Agents extremely powerful and still very easy to get started with.",
  },
  {
    question: "How can I control what my agent says to my clients?",
    answer:
      "You have a wide range of options to customize your agents. You can fine-tune instructions, add restrictions to prevent certain topics, and implement custom behaviors—like defining specific responses or actions based on client input. You can also adjust the agent's personality, tone of voice, response length, and more. Plus, you have full visibility into all conversation history, so you can review interactions and refine your agent's behavior until it aligns with your preferences.",
  },
  {
    question:
      "What makes Hubflo AI Agents unique compared to ChatGPT or other AI solutions?",
    answer:
      "Unlike ChatGPT, which is designed primarily for internal conversational use and can't interact with your leads or clients, Hubflo AI Agents are specifically built for client-facing interactions. They can be integrated into your website or portal and configured to learn about your business in real-time. They also come with preset templates tailored to your industry, making them far more specialized and adaptable than a generic solution like ChatGPT.",
  },
  {
    question: "Can I integrate my AI agents into my website or Hubflo portal?",
    answer:
      "Absolutely! We provide you with a custom script that can easily be embedded into your website, whether it's on Wix, WordPress, or a custom site. This will display the agent as an embedded widget on your site. Your agent can also be accessed through a direct URL link. If you're using a Hubflo client portal, you can seamlessly embed the agent via Google Tag Manager to display it as a widget.",
  },
  {
    question: "Can I integrate Hubflo AI Agents with my existing tools?",
    answer:
      "Yes! We offer integration with your existing tools through Zapier using webhooks. When an agent completes a conversation with a client or lead, you'll receive a comprehensive summary that includes all the client or lead information, an AI-generated summary, and next steps. This summary can be sent to your CRM or used to trigger other automations, making it easy to sync with your workflows.",
  },
  {
    question:
      "How do I get notified when an agent interacts with a lead or a client?",
    answer:
      "At the end of every conversation, you'll receive a comprehensive summary delivered to the email of your choice. Conversations automatically close after 10 minutes of inactivity. The summary includes all collected information, a conversation recap, any tags, and the priority level of the interaction. The AI also flags if any action is required on your end, which you can manage directly from your dashboard. Additionally, you can use our webhook feature to get notifications on platforms like Slack or to send data to your CRM using tools like Zapier, Make, or n8n. It's super easy!",
  },

  {
    question: "What AI model powers Hubflo AI Agents?",
    answer:
      "Our agents are powered by a combination of the latest OpenAI and Claude models, specifically GPT 4.1, GPT 5, and Claude 4. These state-of-the-art models exhibit human-like capabilities, ensuring high-quality interactions. While they're pre-configured by default, we plan to offer customization options in the future, giving you even more control.",
  },

  {
    question: "How secure is my data with Hubflo AI Agents?",
    answer:
      "Your data security is our top priority. We don't train our models on your data, and everything is securely hosted. We never share your information with any third parties. Rest assured, your data is safe with us.",
  },
  {
    question: "Can I export my data or conversation history?",
    answer:
      "Yes! While we don't yet provide a direct option to export all your information from the dashboard (though it's planned), you can reach out to our support team. If you need it, we'll provide a full export of all your chat history and conversations upon request.",
  },
  {
    question: "Can I have multiple internal users on my account?",
    answer:
      "Yes! Now you can invite other internal users to collaborate if you're on Pro plan and higher. The Starter plan allows one user, while Pro and Business plans let you invite additional team members for $19 per user per month.",
  },
  {
    question: "Do voice minutes roll over each month?",
    answer:
      "Voice minutes are set on a monthly basis and do not roll over. For example, if your plan includes 450 voice minutes per month, that's your monthly allocation for phone agent calls. The minute count resets every month. If you exceed your limit, overage charges apply: $0.30/min for Pro plans and $0.21/min for Business plans.",
  },
];

const PricingFAQ = () => {
  return (
    <div className="max-w-4xl mx-auto mt-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Frequently Asked <span style={{ color: "#eab308" }}>Questions</span>
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          Got questions? We've got answers. Find everything you need to know
          about Hubflo AI Agents.
        </p>
      </div>

      <Accordion type="single" collapsible className="w-full space-y-4">
        {faqData.map((faq, index) => (
          <AccordionItem
            key={index}
            value={`item-${index}`}
            className="bg-white rounded-3xl shadow-sm border border-gray-100 px-6"
          >
            <AccordionTrigger className="text-left hover:no-underline py-6 text-lg font-medium text-gray-900 hover:text-[#8aa3d9]">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="pb-6 text-gray-700 leading-relaxed whitespace-pre-line">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default PricingFAQ;
